import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BodyText, Flex } from '@gaiads/telia-react-component-library';
import { AdminCategory, AdminSubcategory } from '@cdaas/api-clients/dist/product-service-openapi';

import { RootState } from '../../../store';
import { fetchAdminCategories, selectAdminCategoriesLoading, selectAllCategories, selectCategoryById } from '../../../store/adminCategories';
import { setNotification } from '../../../store/ui';
import { getInitialAdminCategory, getInitialAdminSubcategory } from '../../../utils/categories';
import { Loading } from '../../../utils/store';
import { AdminSettingsErrorProps } from '../AdminSettings';
import CategoriesList from './CategoriesList/CategoriesList';
import SubcategoriesList from './SubcategoriesList/SubcategoriesList';

interface ProductCategoriesProps extends AdminSettingsErrorProps {
  setIsReady: React.Dispatch<React.SetStateAction<boolean>>;
}
export default function ProductCategories({ errors = {}, setErrors = () => undefined, setIsReady = () => undefined }: ProductCategoriesProps) {
  const { t } = useTranslation('adminSettings');

  const dispatch = useDispatch();

  const [currentCategoryId, setCurrentCategoryId] = useState<AdminCategory['id']>();
  const [newCategory, setNewCategory] = useState<AdminCategory>();
  const [newSubcategory, setNewSubcategory] = useState<AdminSubcategory>();

  const categories = useSelector(selectAllCategories);
  const loading = useSelector(selectAdminCategoriesLoading);
  const currentCategory = useSelector((state: RootState) => (currentCategoryId ? selectCategoryById(state, currentCategoryId) : undefined));

  useEffect(() => {
    dispatch(fetchAdminCategories());
  }, [dispatch]);

  const handleCategoryClick = (category: AdminCategory) => {
    setCurrentCategoryId(category.id);
    setNewSubcategory(undefined);
  };
  const handleCategoryAddition = () => {
    const initialCategory = getInitialAdminCategory();
    setNewCategory(initialCategory);
  };
  const handleSubcategoryAddition = () => {
    const initialSubcategory = getInitialAdminSubcategory();
    setNewSubcategory(initialSubcategory);
  };
  const handleNewCategoryBlur = () => {
    setNewCategory(undefined);
  };
  const handleNewSubcategoryBlur = () => {
    setNewSubcategory(undefined);
  };

  useEffect(() => {
    if (loading === Loading.FAILED) {
      dispatch(
        setNotification({
          text: t('productCategories.errors.fetch'),
          show: true,
          type: 'warning',
        }),
      );
    } else if (loading === Loading.SUCCEEDED) {
      setIsReady(true);
    }
  }, [loading, dispatch, t, setIsReady]);

  return (
    <>
      <BodyText margin={{ bottom: 'md' }}>{t('productCategories.description')}</BodyText>
      <BodyText margin={{ bottom: 'md' }}>{t('productCategories.warning')}</BodyText>
      <Flex className="product-categories__category-columns" stretchToSameHeight pileVerticallyTo="sm">
        <Flex flexItem directionColumn className="product-categories__category-column">
          <BodyText className="product-categories__column-title">{t('productCategories.listTitles.categories')}</BodyText>
          <CategoriesList
            errors={errors}
            setErrors={setErrors}
            categories={categories}
            newCategory={newCategory}
            currentCategoryId={currentCategoryId}
            handleCategoryClick={handleCategoryClick}
            handleCategoryAddition={handleCategoryAddition}
            handleNewCategoryBlur={handleNewCategoryBlur}
          />
        </Flex>
        <Flex flexItem directionColumn className="product-categories__subcategory-column">
          <BodyText className="product-categories__column-title">{t('productCategories.listTitles.subcategories')}</BodyText>
          <SubcategoriesList
            errors={errors}
            setErrors={setErrors}
            currentCategory={currentCategory}
            newSubcategory={newSubcategory}
            handleSubcategoryAddition={handleSubcategoryAddition}
            handleNewSubcategoryBlur={handleNewSubcategoryBlur}
          />
        </Flex>
      </Flex>
    </>
  );
}
